import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
  type Firestore,
} from 'firebase/firestore';

import { COLLECTIONS } from '@/lib/firebase/paths';
import { repairTextArtifactsInValue } from '@/lib/text-encoding';
import type { TitleDoc } from '@/types/platform';

type TimestampLike = { toMillis?: () => number; seconds?: number };

type ManualReviewRecord = {
  reviewId?: string;
  authorName?: string;
  rating?: number;
  body?: string;
  source?: string;
  status?: string;
};

function hydrateTitle(rawRecord: Partial<TitleDoc>, id: string): TitleDoc {
  const record = repairTextArtifactsInValue(rawRecord);
  const reviews = Array.isArray(record.reviews)
    ? record.reviews
        .map((review: ManualReviewRecord, index: number) => ({
          reviewId: String(review.reviewId || `manual-${index + 1}`),
          authorName: String(review.authorName || 'Reader'),
          rating: Math.min(5, Math.max(1, Number(review.rating || 5))),
          body: String(review.body || '').trim(),
          source: review.source ? String(review.source) : undefined,
          status: review.status === 'hidden' ? 'hidden' as const : 'visible' as const,
        }))
        .filter((review) => review.body)
    : [];
  const visibleReviews = reviews.filter((review) => review.status !== 'hidden');
  const baseline = Math.max(0, Number(record.salesCountBaseline || 0));
  const actual = Math.max(0, Number(record.salesCountActual || 0));
  const averageRating = visibleReviews.length
    ? visibleReviews.reduce((sum: number, review) => sum + Number(review.rating || 0), 0) / visibleReviews.length
    : Number(record.averageRating || 0);
  return {
    ...(record as TitleDoc),
    titleId: record.titleId ?? id,
    formats: Array.isArray(record.formats) ? record.formats : ['ebook'],
    featured: Boolean(record.featured),
    tags: Array.isArray(record.tags) ? record.tags : [],
    keywords: Array.isArray(record.keywords) ? record.keywords : [],
    featureBullets: Array.isArray(record.featureBullets) ? record.featureBullets : [],
    salesCountBaseline: baseline,
    salesCountActual: actual,
    salesCount: Number(record.salesCount ?? baseline + actual),
    reviews,
    reviewCount: Number(record.reviewCount ?? visibleReviews.length),
    averageRating,
  };
}

function timestampToMillis(value: unknown): number {
  if (!value) return 0;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (typeof value === 'object' && value) {
    const timestamp = value as TimestampLike;
    if (typeof timestamp.toMillis === 'function') return timestamp.toMillis();
    if (typeof timestamp.seconds === 'number') return timestamp.seconds * 1000;
  }
  return 0;
}

function sortByRecent<T extends { publishedAt?: unknown; updatedAt?: unknown; createdAt?: unknown }>(items: T[]) {
  return [...items].sort((a, b) => {
    const aValue = timestampToMillis(a.publishedAt ?? a.updatedAt ?? a.createdAt);
    const bValue = timestampToMillis(b.publishedAt ?? b.updatedAt ?? b.createdAt);
    return bValue - aValue;
  });
}

// Returns only live Firestore titles that pass storefront visibility and publication checks.
// Use this when you need a strict view of what exists in the database (admin panels, APIs).
export async function listFirestoreTitles(db: Firestore, size = 48) {
  const snapshot = await getDocs(query(collection(db, COLLECTIONS.titles), orderBy('updatedAt', 'desc'), limit(size)));
  return snapshot.docs
    .map((item) => hydrateTitle(item.data(), item.id))
    .filter((item) => item.storefrontVisibility && ['approved', 'published'].includes(item.publicationStatus));
}

// Returns only Firestore-backed public titles for production storefront surfaces.
export async function listPublicTitles(db: Firestore, size = 48) {
  const snapshot = await getDocs(query(
    collection(db, COLLECTIONS.titles),
    where('storefrontVisibility', '==', true),
    where('publicationStatus', 'in', ['approved', 'published']),
    orderBy('updatedAt', 'desc'),
    limit(size),
  ));
  return snapshot.docs.map((item) => hydrateTitle(item.data(), item.id));
}

export async function listAllTitles(db: Firestore) {
  const snapshot = await getDocs(collection(db, COLLECTIONS.titles));
  return sortByRecent(snapshot.docs.map((item) => hydrateTitle(item.data(), item.id)));
}

export async function listTitlesForOwner(db: Firestore, ownerUid: string, size = 48) {
  const snapshot = await getDocs(query(collection(db, COLLECTIONS.titles), where('ownerUid', '==', ownerUid), limit(size)));
  return snapshot.docs
    .map((item) => hydrateTitle(item.data(), item.id))
    .sort((a, b) => timestampToMillis(b.updatedAt) - timestampToMillis(a.updatedAt));
}

export async function listTitlesForAuthor(db: Firestore, authorProfileId: string, size = 48) {
  const snapshot = await getDocs(query(collection(db, COLLECTIONS.titles), where('authorProfileId', '==', authorProfileId), limit(size)));
  return snapshot.docs
    .map((item) => hydrateTitle(item.data(), item.id))
    .filter((item) => item.storefrontVisibility && ['approved', 'published'].includes(item.publicationStatus))
    .sort((a, b) => timestampToMillis(b.updatedAt) - timestampToMillis(a.updatedAt));
}

export async function getTitleById(db: Firestore, titleId: string) {
  const snapshot = await getDoc(doc(db, COLLECTIONS.titles, titleId));
  if (!snapshot.exists()) return null;
  return hydrateTitle(snapshot.data(), snapshot.id);
}

export async function getTitleBySlug(db: Firestore, slug: string) {
  const snapshot = await getDocs(query(collection(db, COLLECTIONS.titles), where('slug', '==', slug), limit(1)));
  const match = snapshot.docs[0];
  if (match) {
    const live = hydrateTitle(match.data(), match.id);
    if (live.storefrontVisibility && ['approved', 'published'].includes(live.publicationStatus)) return live;
    return null;
  }
  return null;
}

export async function getPublicTitleBySlug(db: Firestore, slug: string) {
  const snapshot = await getDocs(query(
    collection(db, COLLECTIONS.titles),
    where('slug', '==', slug),
    where('storefrontVisibility', '==', true),
    where('publicationStatus', 'in', ['approved', 'published']),
    limit(1),
  ));
  const match = snapshot.docs[0];
  return match ? hydrateTitle(match.data(), match.id) : null;
}

export async function getTitleBySourceProjectId(db: Firestore, projectId: string) {
  const snapshot = await getDocs(query(collection(db, COLLECTIONS.titles), where('sourceProjectId', '==', projectId), limit(1)));
  const match = snapshot.docs[0];
  if (!match) return null;
  return hydrateTitle(match.data(), match.id);
}
